import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import { Mic } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_API_URL || '';

function RequestBlock({ block, isLive }) {
  const theme = useTheme();
  const maxVotes = block.requests?.[0]?.request_count || 1;

  return (
    <div style={{
      borderRadius: 12,
      border: `2px solid ${theme.primary}`,
      background: `${theme.primary}10`,
      overflow: 'hidden',
      margin: '0.25rem 0',
    }}>
      <div style={{
        background: theme.primary,
        padding: '0.6rem 1rem',
        display: 'flex', alignItems: 'center', gap: '0.5rem',
      }}>
        <Mic size={16} color="#fff" />
        <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>Audience Requests</span>
        <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
          {block.duration_minutes || 20} mins
        </span>
      </div>

      {isLive && block.requests?.length > 0 ? (
        <div style={{ padding: '0.5rem 0' }}>
          {block.requests.map((req, idx) => (
            <div key={req.song_id} style={{
              padding: '0.55rem 1rem',
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              borderBottom: idx < block.requests.length - 1 ? `1px solid ${theme.border}` : 'none',
            }}>
              <div style={{
                fontSize: '0.8rem', fontWeight: 700,
                color: idx === 0 ? theme.primary : theme.textMuted,
                minWidth: 18, textAlign: 'right',
              }}>
                {idx + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 600, fontSize: '1rem',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  color: theme.text,
                }}>
                  {req.title}
                </div>
                <div style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: 1 }}>
                  {req.artist_original}
                </div>
              </div>
              <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <div style={{
                  height: 6, borderRadius: 3,
                  width: Math.max(24, Math.round((req.request_count / maxVotes) * 60)),
                  background: theme.primary,
                  opacity: 0.6 + (req.request_count / maxVotes) * 0.4,
                }} />
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: theme.primary, minWidth: 20, textAlign: 'right' }}>
                  {req.request_count}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ padding: '1rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.9rem' }}>
          {isLive ? 'No requests yet' : 'Live requests will appear here'}
        </div>
      )}
    </div>
  );
}

export default function BandView() {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSet, setCurrentSet] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/band-view`);
      setData(res.data);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const uniqueUrl = data?.gig?.unique_url;
  useEffect(() => {
    if (!uniqueUrl) return;
    const socket = io(API_URL);
    socket.emit('joinGig', uniqueUrl);
    socket.on('newRequest', fetchData);
    socket.on('requestsCleared', fetchData);
    socket.on('requestStatusChanged', fetchData);
    socket.on('setListUpdated', fetchData);
    return () => socket.disconnect();
  }, [uniqueUrl, fetchData]);

  if (loading) {
    return (
      <div style={{ minHeight: '100dvh', background: theme.background, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: theme.textMuted, fontSize: '0.9rem' }}>Loading…</div>
      </div>
    );
  }

  const { band, gig, setList } = data || {};
  const numberOfSets = gig?.number_of_sets || 1;
  const slots = setList?.[currentSet] || [];
  const isLive = gig?.is_active && gig?.is_accepting_requests;

  return (
    <div style={{
      minHeight: '100dvh',
      background: theme.background,
      color: theme.text,
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      {/* Header */}
      <div style={{ background: theme.navGradient, padding: '16px 20px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, maxWidth: 600, margin: '0 auto' }}>
          {band?.logo_url && (
            <img src={band.logo_url} alt="Band logo" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8, background: 'rgba(255,255,255,0.15)', flexShrink: 0 }} />
          )}
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#fff', lineHeight: 1.2 }}>
              {band?.band_name || 'Set List'}
            </div>
            {gig && (
              <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                {gig.venue_name}
                {gig.gig_date && ` · ${new Date(gig.gig_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}`}
              </div>
            )}
          </div>
          {isLive && (
            <div style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '0.2rem 0.65rem', fontSize: '0.72rem', fontWeight: 700, color: '#fff', letterSpacing: '0.05em' }}>
              LIVE
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '0 0 40px' }}>

        {!gig ? (
          <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: theme.textMuted }}>
            <Mic size={36} style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
            <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '0.25rem' }}>No active gig tonight</div>
            <div style={{ fontSize: '0.85rem' }}>Check back when the gig starts</div>
          </div>
        ) : (
          <>
            {/* Set tabs */}
            {numberOfSets > 1 && (
              <div style={{ display: 'flex', borderBottom: `1px solid ${theme.border}` }}>
                {[...Array(numberOfSets)].map((_, idx) => (
                  <button key={idx} onClick={() => setCurrentSet(idx + 1)}
                    style={{
                      flex: 1, padding: '0.75rem 0', background: 'none', border: 'none', cursor: 'pointer',
                      color: currentSet === idx + 1 ? theme.primary : theme.textMuted,
                      borderBottom: `2px solid ${currentSet === idx + 1 ? theme.primary : 'transparent'}`,
                      fontWeight: currentSet === idx + 1 ? 700 : 400, fontSize: '0.9rem',
                      marginBottom: -1,
                    }}>
                    Set {idx + 1}
                  </button>
                ))}
              </div>
            )}

            {/* Set list */}
            <div style={{ padding: '0.75rem 1rem' }}>
              {slots.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.9rem' }}>
                  Set list not built yet
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {slots.map((slot, idx) => {
                    if (slot.slot_type === 'request_block') {
                      return <RequestBlock key={slot.id} block={slot} isLive={isLive} />;
                    }
                    const songNum = slots.slice(0, idx).filter(s => s.slot_type !== 'request_block').length + 1;
                    return (
                      <div key={slot.id} style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '0.6rem 0.25rem',
                        borderBottom: `1px solid ${theme.border}`,
                      }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: 700, color: theme.textMuted, minWidth: 24, textAlign: 'right', flexShrink: 0 }}>
                          {songNum}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '1.05rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {slot.title}
                          </div>
                          <div style={{ fontSize: '0.82rem', color: theme.textMuted, marginTop: 1 }}>
                            {slot.artist_original}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
